"use client";

import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { useTradeStore } from "@/stores/tradeStore";

const indicatorPalette = {
  SMA: "#6366f1",
  EMA: "#ec4899",
};

export const IndicatorsPanel = () => {
  const { indicators, addIndicator, toggleIndicatorVisibility, clearIndicators } =
    useTradeStore();
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<"SMA" | "EMA">("SMA");
  const [period, setPeriod] = useState(20);

  const handleAdd = () => {
    const id = `${selected}-${period}`;
    addIndicator({
      id,
      type: selected,
      period,
      color: indicatorPalette[selected],
      visible: true,
    });
    setShowModal(false);
  };

  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowModal(true)}
          className="rounded-lg bg-slate-900 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
        >
          Indicators
        </button>
        {indicators.length ? (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {indicators.map((ind) => (
              <button
                key={ind.id}
                onClick={() => toggleIndicatorVisibility(ind.id)}
                aria-label={`Toggle ${ind.id}`}
                className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700 hover:border-slate-300"
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: ind.color }}
                />
                {ind.type}({ind.period})
                {!ind.visible ? <span className="text-slate-400">(off)</span> : null}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-500">No indicators</p>
        )}
      </div>
      {indicators.length ? (
        <button
          onClick={clearIndicators}
          className="text-xs font-semibold text-rose-600 hover:text-rose-700"
        >
          Clear All
        </button>
      ) : null}

      {showModal ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-500">Add Indicator</p>
                <h3 className="text-lg font-bold text-slate-900">Overlay & Oscillator</h3>
              </div>
              <button
                aria-label="Close indicator modal"
                onClick={() => setShowModal(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <label className="block text-sm font-semibold text-slate-700">
                Indicator
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value as "SMA" | "EMA")}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="SMA">Simple Moving Average</option>
                  <option value="EMA">Exponential Moving Average</option>
                </select>
              </label>

              <label className="block text-sm font-semibold text-slate-700">
                Period
                <input
                  type="number"
                  min={2}
                  max={200}
                  value={period}
                  onChange={(e) => setPeriod(Number(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
